import React, { useState, useCallback, useMemo } from 'react';

import {
  format,
  subDays,
  setHours,
  setMinutes,
  setSeconds,
  parse,
} from 'date-fns';
import { isNull, forEach, assign } from 'lodash';
import * as Yup from 'yup';

import { t, parseDateString } from '~/helpers';
import { Report } from '~/components';
import {
  DateTime,
  Autocomplete,
  Input,
  Select,
  RadioGroup,
} from '~/components/Form';

export default function AbastecimentoContigenciaLista() {
  const [open, setOpen] = useState(true);
  const [organ, setOrgan] = useState({ orga_codigo: '', orga_nome: '' });
  const [userPost, setUserPost] = useState({
    poco_codigo: '',
    poco_descricao: '',
  });
  const [unity, setUnity] = useState({ unid_codigo: '', unid_nome: '' });
  const [beak, setBeak] = useState({ bico_numero: '', bico_descricao: '' });
  const [device, setDevice] = useState('');

  const datahorainicial = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(subDays(new Date(), 7), 0), 0), 0),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const datahorafinal = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(new Date(), 23), 59), 59),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const devices = useMemo(
    () => [
      { label: 'Todos', value: 1 },
      { label: 'Com Dispositivo', value: 2 },
      { label: 'Sem Dispositivo', value: 3 },
    ],
    []
  );

  const handleCloseReport = useCallback(() => {
    setOpen(false);
  }, []);

  const handleValidate = useCallback(
    async (data) => {
      try {
        const schema = Yup.object().shape({
          datahorainicial: Yup.date()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .transform((_, value) => parseDateString(value))
            .required(t('s.geral.validacao.campoobrigatorio')),
          datahorafinal: Yup.date()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .transform((_, value) => parseDateString(value))
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(
              datahorafinal,
              `A data não pode ser superior a ${datahorafinal}`
            )
            .min(
              Yup.ref('datahorainicial'),
              'A data não pode ser inferior a Data inicial'
            ),
        });

        await schema.validate(data, { abortEarly: false });
      } catch (err) {
        const form = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            form[error.path] = error.message.replace(`${error.path} `, '');
          });

          throw { form };
        }
      }
    },
    [datahorafinal]
  );

  const onSerializeData = useCallback(
    (data) => {
      const formData = assign(data, {
        orgaostr: organ.orga_nome ?? '',
        postostr: userPost.poco_descricao ?? '',
        unidade: unity.unid_codigo ?? '',
        unidadestr: unity.unid_nome ?? '',
        bico: beak.bico_numero ?? '',
        bicostr: beak.bico_descricao ?? '',
        dispositivoinstaladostr: device,
      });

      forEach(Object.keys(formData), (value) => {
        if (!formData[value]) delete formData[value];
      });

      return formData;
    },
    [organ, userPost, unity, beak, device]
  );

  const handleChangeUnity = useCallback((value) => {
    if (isNull(value.unid_nome)) setUnity({ unid_codigo: '', unid_nome: '' });
    else setUnity(value);
  }, []);

  const handleChangeBeak = useCallback((value) => {
    if (isNull(value.bico_numero))
      setBeak({ bico_numero: '', bico_descricao: '' });
    else setBeak(value);
  }, []);

  const handleInputChangeUnity = useCallback(
    (value) => {
      if (organ.orga_codigo) {
        setUnity((state) => ({ ...state, unid_nome: value }));
      }
    },
    [organ]
  );

  const handleInputChangeBeak = useCallback(
    (value) => {
      if (userPost.poco_codigo) {
        setBeak((state) => ({ ...state, bico_descricao: value }));
      }
    },
    [userPost]
  );

  return (
    <Report
      open={open}
      onClose={handleCloseReport}
      serialize={onSerializeData}
      validate={handleValidate}
      title="Abastecimento Contigência Lista"
      relatorio="ABASTECIMENTOCONTIGENCIA"
      initials={{
        datahorainicial,
        datahorafinal,
        tipo: 'pdf',
        dispositivoinstalado: 1,
      }}
    >
      <DateTime
        time
        disableFuture
        label="Data Inicial"
        name="datahorainicial"
      />

      <DateTime
        time
        disableFuture
        label="Data Final"
        name="datahorafinal"
        maxDateMessage=""
      />

      <Autocomplete
        name="orgao"
        label="orga_nome"
        title="Órgão"
        url="orgaos"
        unique="orga_codigo"
        getValue={(value) => {
          setOrgan(value);
          setUnity({ unid_codigo: '', unid_nome: '' });
        }}
      />

      <Autocomplete
        includeInputInList
        disabledItemsFocusable
        disableClearable={!unity.unid_codigo}
        name="unidade"
        label="unid_nome"
        title="Unidade"
        url="unidades"
        unique="unid_codigo"
        params={{
          wherefield: ['orga_codigo'],
          valuewherefield: [organ.orga_codigo],
          typewherefield: ['EQUALS'],
        }}
        disabled={isNull(organ.orga_codigo)}
        getValue={handleChangeUnity}
        inputValue={unity.unid_nome}
        onInputChange={(e, value) => handleInputChangeUnity(value)}
      />

      <Autocomplete
        name="posto"
        label="poco_desc_abrev"
        title="Posto"
        url="postosporusuario"
        unique="poco_codigo"
        getValue={(value) => {
          setUserPost(value);
          setBeak({ bico_numero: null, bico_descricao: '' });
        }}
      />

      <Autocomplete
        disableClearable={!beak.bico_numero}
        name="bico"
        label="bico_descricao"
        title="Bico"
        url="bicos"
        unique="bico_numero"
        params={{
          wherefield: ['poco_codigo'],
          valuewherefield: [userPost.poco_codigo],
          typewherefield: ['EQUALS'],
        }}
        disabled={isNull(userPost.poco_codigo)}
        getValue={handleChangeBeak}
        inputValue={beak.bico_descricao}
        onInputChange={(e, value) => handleInputChangeBeak(value)}
      />

      <Input name="placa" label="Placa" />

      <Select
        name="dispositivoinstalado"
        label="Dispositivo Instalado"
        options={devices}
        getValue={(value) =>
          setDevice(
            value ? devices.find((dev) => dev.value === value).label : ''
          )
        }
      />

      <RadioGroup
        row
        name="tipo"
        label="Formato"
        options={[
          { label: 'PDF', value: 'pdf' },
          { label: 'Excel', value: 'xls' },
        ]}
      />
    </Report>
  );
}
